import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputAdornment,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import AutoFixHighRoundedIcon from '@mui/icons-material/AutoFixHighRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import PhoneIphoneRoundedIcon from '@mui/icons-material/PhoneIphoneRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import { toast } from 'react-toastify';

import api from '../../services/api';
import { getRoleApiPrefix } from '../../utils/roleCapabilities';
import { PageHeader, PageLayout, SectionCard } from '../../components/ui';

// Leave types use the backend enum as the canonical *value*, with a
// human-readable *label* for the Select. Keeping these in lockstep here
// prevents the old mismatch where the UI sent "Passing away (Family)"
// while the API only accepts "FamilyDeath".
const LEAVE_TYPES = [
  { value: 'Annual',       label: 'Annual' },
  { value: 'Sick',         label: 'Sick' },
  { value: 'Emergency',    label: 'Emergency' },
  { value: 'Unpaid',       label: 'Unpaid' },
  { value: 'BabyBorn',     label: 'BabyBorn' },
  { value: 'Exam',         label: 'Exam' },
  { value: 'Haj',          label: 'Haj' },
  { value: 'Marriage',     label: 'Marriage' },
  { value: 'Pregnancy',    label: 'Pregnancy' },
  { value: 'Payroll',      label: 'Payroll' },
  { value: 'FamilyDeath',  label: 'Passing away (Family)' },
  { value: 'HusbandDeath', label: 'Passing away (Husband)' },
  { value: 'WorkVacation', label: 'Work Vacation' },
];

const labelForLeaveType = (value) =>
  LEAVE_TYPES.find((item) => item.value === value)?.label || value;

// Leave types where a supporting document is always required (uses canonical
// backend values to stay in sync with VacationRequestController@store).
const ATTACHMENT_REQUIRED_TYPES = new Set([
  'Marriage',
  'BabyBorn',
  'Haj',
  'Pregnancy',
  'FamilyDeath',
  'HusbandDeath',
]);

// Leave types that may be filed with past start dates (e.g. medical / urgent).
const BACK_DATE_ALLOWED_TYPES = new Set([
  'Sick',
  'Emergency',
  'FamilyDeath',
  'HusbandDeath',
]);

// Sick leave longer than this requires a medical certificate.
const SICK_ATTACHMENT_DAY_THRESHOLD = 2;

// Endpoint that returns the *current user's* vacation requests, used to
// detect pending submissions. Most role-prefixed groups expose
// `/vacation-requests2/index`; the plain `/employee` group uses the
// shorter `/vacation-requests` route.
const VACATION_LIST_PATH_BY_PREFIX = {
  '/employee': '/vacation-requests',
};
const resolveVacationListPath = (apiPrefix) =>
  VACATION_LIST_PATH_BY_PREFIX[apiPrefix] || '/vacation-requests2/index';

const requiresAttachment = (leaveType, days) => {
  if (ATTACHMENT_REQUIRED_TYPES.has(leaveType)) return true;
  if (leaveType === 'Sick' && days > SICK_ATTACHMENT_DAY_THRESHOLD) return true;
  return false;
};

const initialVacationForm = {
  leave_type: 'Annual',
  start_date: '',
  end_date: '',
  country: '',
  phone_number: '',
  location_in_country: '',
  description: '',
};

// Default approval chain shown until the live preview-routing endpoint
// responds. Replaced at runtime with real approver names where available.
const DEFAULT_VACATION_WORKFLOW_STEPS = [
  { actor: 'Employee', role: 'Employee', action: 'Submit vacation request with leave window and details.' },
  { actor: 'Manager / Finance', role: 'Manager / Finance', action: 'First approval or rejection decision.' },
  { actor: 'HR Admin', role: 'HR Admin', action: 'Final approval or rejection.' },
  { actor: 'System', role: 'System', action: 'Notify requester and update balance status.' },
];

function getRoutePrefixByRole() {
  return getRoleApiPrefix(localStorage.getItem('role'));
}

function getDailyAccrualRate(contractType, leaveType) {
  if (leaveType !== 'Annual') return 0;
  switch (contractType) {
    case '30 Days Yearly':
      return 30 / 365;
    case '24 Days Yearly':
      return 24 / 365;
    case '21 Days Yearly':
      return 21 / 365;
    case '20 Days Yearly':
      return 20 / 365;
    default:
      return 0;
  }
}

function getInclusiveDaysBetween(startStr, endStr) {
  if (!startStr || !endStr) return 0;
  const start = new Date(startStr);
  const end = new Date(endStr);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return 0;
  const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
  const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate()).getTime();
  return Math.floor((endDay - startDay) / 86400000) + 1;
}

function getLeaveTypesForGender(gender) {
  if ((gender || '').toLowerCase() === 'male') {
    return LEAVE_TYPES.filter((item) => item.value !== 'Pregnancy' && item.value !== 'HusbandDeath');
  }
  return LEAVE_TYPES;
}

function getAccrualUntilYearEnd(contractType, leaveType, currentBase) {
  if (leaveType !== 'Annual') return { accrualDays: 0, potentialTotal: currentBase };
  const dailyRate = getDailyAccrualRate(contractType, leaveType);
  if (dailyRate === 0) return { accrualDays: 0, potentialTotal: currentBase };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const endOfYear = new Date(today.getFullYear(), 11, 31);
  if (endOfYear < today) return { accrualDays: 0, potentialTotal: currentBase };

  const days = getInclusiveDaysBetween(today.toISOString().split('T')[0], endOfYear.toISOString().split('T')[0]);
  const accrualDays = days * dailyRate;
  return { accrualDays, potentialTotal: currentBase + accrualDays };
}

function FieldGroup({ icon, title, description, children }) {
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1 }}>
        {icon}
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
            {title}
          </Typography>
          {description ? (
            <Typography variant="caption" color="text.secondary">
              {description}
            </Typography>
          ) : null}
        </Box>
      </Stack>
      {children}
    </Box>
  );
}

function FieldGrid({ children }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
        gap: 1.2,
      }}
    >
      {children}
    </Box>
  );
}

function SummaryTile({ label, value, hint, tone = 'primary' }) {
  const theme = useTheme();
  const color = theme.palette[tone]?.main || theme.palette.primary.main;

  return (
    <Box
      sx={{
        p: 1,
        borderRadius: theme.hr?.radius?.md || 2,
        border: '1px solid',
        borderColor: alpha(color, 0.16),
        bgcolor: alpha(color, theme.palette.mode === 'dark' ? 0.11 : 0.055),
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 850 }}>
        {label}
      </Typography>
      <Typography variant="h6" sx={{ mt: 0.2, fontWeight: 900, lineHeight: 1.15 }}>
        {value}
      </Typography>
      {hint ? (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      ) : null}
    </Box>
  );
}

function SummaryRow({ icon, label, value }) {
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Box sx={{ color: 'text.secondary', display: 'grid', placeItems: 'center', mt: 0.1 }}>{icon}</Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 850, overflowWrap: 'anywhere' }}>
          {value || '-'}
        </Typography>
      </Box>
    </Stack>
  );
}

function VacationRequestForm() {
  const navigate = useNavigate();
  const theme = useTheme();

  const gender = localStorage.getItem('gender') || '';
  const apiPrefix = getRoutePrefixByRole();
  const todayStr = new Date().toISOString().split('T')[0];

  const [formData, setFormData] = useState(initialVacationForm);
  const [vacationBalances, setVacationBalances] = useState([]);
  const [baseBalance, setBaseBalance] = useState(0);
  const [contractType, setContractType] = useState('');
  const [requestedDays, setRequestedDays] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [suggestedEndDate, setSuggestedEndDate] = useState(null);
  const [hasPendingRequest, setHasPendingRequest] = useState(false);
  const [file, setFile] = useState(null);
  const [routingSteps, setRoutingSteps] = useState(DEFAULT_VACATION_WORKFLOW_STEPS);

  useEffect(() => {
    api
      .get('/vacation-requests/preview-routing')
      .then((response) => {
        const steps = Array.isArray(response.data?.steps) ? response.data.steps : [];
        if (steps.length) setRoutingSteps(steps);
      })
      .catch((err) => {
        // Non-fatal — fall back to the default static chain.
        console.warn('Vacation routing preview unavailable:', err?.response?.status || err);
      });
  }, []);

  useEffect(() => {
    api
      .get('/vacation-balances/my')
      .then((response) => {
        const rows = Array.isArray(response.data) ? response.data : [];
        if (!rows.length) {
          setVacationBalances([]);
          setBaseBalance(0);
          setContractType('');
          return;
        }

        setVacationBalances(rows);
        const firstItem = rows[0];
        if (firstItem?.user?.contract_type) {
          setContractType(firstItem.user.contract_type);
        }

        const annualBalance = rows.find((balance) => balance.leave_type === 'Annual');
        setBaseBalance(annualBalance ? parseFloat(annualBalance.balance) || 0 : 0);
      })
      .catch((err) => {
        console.error('Error fetching /vacation-balances/my:', err);
        setError('Failed to fetch vacation balances.');
      });
  }, []);

  useEffect(() => {
    api
      .get(`${apiPrefix}${resolveVacationListPath(apiPrefix)}`)
      .then((response) => {
        const rows = Array.isArray(response.data) ? response.data : [];
        const pending = rows.some((request) => request.status === 'pending' || request.hr_status === 'pending');
        setHasPendingRequest(pending);
      })
      .catch((err) => {
        console.error('Error fetching vacation requests:', err);
      });
  }, [apiPrefix]);

  function getMaxPossibleVacation(endDateStr) {
    const dailyRate = getDailyAccrualRate(contractType, formData.leave_type);
    if (dailyRate === 0 || !endDateStr) return baseBalance;
    const accrualDays = getInclusiveDaysBetween(todayStr, endDateStr);
    return baseBalance + accrualDays * dailyRate;
  }

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === 'start_date' || name === 'end_date') {
      const startDate = name === 'start_date' ? value : formData.start_date;
      const endDate = name === 'end_date' ? value : formData.end_date;
      const days = getInclusiveDaysBetween(startDate, endDate);
      setRequestedDays(days);
      setSuggestedEndDate(days <= getMaxPossibleVacation(endDate) ? endDate : null);
    }

    if (name === 'leave_type') {
      const match = vacationBalances.find((balance) => balance.leave_type === value);
      setBaseBalance(match ? parseFloat(match.balance) || 0 : 0);
    }
  };

  function suggestEndDate() {
    if (!formData.start_date) return;
    const start = new Date(formData.start_date);
    if (Number.isNaN(start.getTime())) return;

    const maxDaysPossible = Math.floor(getMaxPossibleVacation(formData.end_date || todayStr));
    if (maxDaysPossible <= 0) return;

    const end = new Date(start);
    end.setDate(start.getDate() + (maxDaysPossible - 1));
    const endStr = end.toISOString().split('T')[0];

    setSuggestedEndDate(endStr);
    setFormData((prev) => ({ ...prev, end_date: endStr }));
    setRequestedDays(getInclusiveDaysBetween(formData.start_date, endStr));
  }

  const handleFileChange = (event) => {
    setFile(event.target.files?.[0] || null);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (hasPendingRequest) {
      toast.error('You already have a vacation request pending approval.');
      return;
    }

    // Date sanity (required fields are already enforced by the form's `required`
    // attribute; if they are present we still re-check the ordering and bounds).
    if (formData.start_date && formData.end_date && formData.end_date < formData.start_date) {
      toast.error('End date cannot be before start date.');
      return;
    }
    if (
      formData.start_date &&
      formData.start_date < todayStr &&
      !BACK_DATE_ALLOWED_TYPES.has(formData.leave_type)
    ) {
      toast.error(`${labelForLeaveType(formData.leave_type)} cannot be back-dated.`);
      return;
    }

    const skipBalanceCheck = ['Emergency', 'Unpaid', 'WorkVacation'].includes(formData.leave_type);
    const maxPossible = getMaxPossibleVacation(formData.end_date);
    if (!skipBalanceCheck && requestedDays > maxPossible) {
      toast.error('Not enough balance (including accrual) for that many days.');
      return;
    }

    if (requiresAttachment(formData.leave_type, requestedDays) && !file) {
      toast.error(`${labelForLeaveType(formData.leave_type)} requests require a supporting document.`);
      return;
    }

    setLoading(true);
    setError('');
    setValidationErrors([]);

    const formDataWithFile = new FormData();
    Object.keys(formData).forEach((key) => {
      formDataWithFile.append(key, formData[key]);
    });
    if (file) {
      formDataWithFile.append('attachment', file);
    }

    api
      .post(`${apiPrefix}/vacation-requests`, formDataWithFile, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(() => {
        toast.success('Vacation request submitted successfully!');
        setFormData(initialVacationForm);
        setRequestedDays(0);
        setSuggestedEndDate(null);
        setFile(null);
      })
      .catch((err) => {
        if (err.response?.status === 422) {
          const data = err.response.data || {};
          if (data.errors) {
            const messages = [];
            Object.keys(data.errors).forEach((field) => {
              data.errors[field].forEach((msg) => messages.push(msg));
            });
            setValidationErrors(messages);
          } else {
            setValidationErrors([data.message || 'Validation failed.']);
          }
        } else if (err.response?.status === 401 || err.response?.status === 403) {
          const detail =
            err.response?.data?.message ||
            "You aren't authorized to submit this vacation request. Please contact HR if this is unexpected.";
          console.error(err);
          setError(detail);
          toast.error(detail);
        } else {
          console.error(err);
          const detail = err.response?.data?.message || 'Failed to submit vacation request.';
          setError(detail);
          toast.error(detail);
        }
      })
      .finally(() => setLoading(false));
  };

  const handleReset = () => {
    setFormData(initialVacationForm);
    setRequestedDays(0);
    setSuggestedEndDate(null);
    setFile(null);
    setValidationErrors([]);
    setError('');
  };

  const maxPossibleVacation = getMaxPossibleVacation(formData.end_date);
  const { accrualDays, potentialTotal } = getAccrualUntilYearEnd(contractType, formData.leave_type, baseBalance);
  const skipBalanceCheck = ['Emergency', 'Unpaid', 'WorkVacation'].includes(formData.leave_type);
  const exceedsBalance = !skipBalanceCheck && requestedDays > maxPossibleVacation;
  const usagePct = maxPossibleVacation > 0 ? Math.min(100, Math.round((requestedDays / maxPossibleVacation) * 100)) : 0;
  const attachmentNeeded = requiresAttachment(formData.leave_type, requestedDays);
  const endBeforeStart =
    Boolean(formData.start_date && formData.end_date && formData.end_date < formData.start_date);
  const startInPast =
    Boolean(
      formData.start_date &&
        formData.start_date < todayStr &&
        !BACK_DATE_ALLOWED_TYPES.has(formData.leave_type)
    );
  const requiredStatus = useMemo(
    () => [
      { label: 'Leave type', done: Boolean(formData.leave_type) },
      { label: 'Start date', done: Boolean(formData.start_date) },
      { label: 'End date', done: Boolean(formData.end_date) },
    ],
    [formData.leave_type, formData.start_date, formData.end_date]
  );
  const completionPct = Math.round((requiredStatus.filter((item) => item.done).length / requiredStatus.length) * 100);

  // Aggregate every reason the submit button could be blocked, in the
  // priority order we want surfaced to the user (first reason wins for the
  // tooltip; the full list renders inline under the progress bar).
  const blockingReasons = useMemo(() => {
    const reasons = [];
    if (loading) reasons.push('Submitting…');
    if (hasPendingRequest) reasons.push('You already have a pending vacation request.');
    requiredStatus.forEach((item) => {
      if (!item.done) reasons.push(`Missing required field: ${item.label}.`);
    });
    if (endBeforeStart) reasons.push('End date is before start date.');
    if (startInPast) reasons.push(`${labelForLeaveType(formData.leave_type)} cannot be back-dated.`);
    if (exceedsBalance) {
      const diff = Math.max(0, Math.ceil(requestedDays - maxPossibleVacation));
      reasons.push(
        diff > 0
          ? `Exceeds available balance by ${diff} day${diff === 1 ? '' : 's'}.`
          : 'Exceeds available balance.'
      );
    }
    if (attachmentNeeded && !file) {
      reasons.push(
        formData.leave_type === 'Sick'
          ? `Sick leave longer than ${SICK_ATTACHMENT_DAY_THRESHOLD} day${SICK_ATTACHMENT_DAY_THRESHOLD === 1 ? '' : 's'} requires a medical certificate.`
          : `${labelForLeaveType(formData.leave_type)} requires a supporting document.`
      );
    }
    return reasons;
  }, [
    loading,
    hasPendingRequest,
    requiredStatus,
    endBeforeStart,
    startInPast,
    exceedsBalance,
    requestedDays,
    maxPossibleVacation,
    attachmentNeeded,
    file,
    formData.leave_type,
  ]);

  const canSubmit = blockingReasons.length === 0;
  const disabledReason = blockingReasons[0] || '';

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.hr?.radius?.md || 2,
      backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.74 : 0.92),
    },
  };

  return (
    <PageLayout backgroundAccent compact maxWidth="xl">
      <PageHeader
        compact
        variant="dashboard"
        overline="Requests"
        title="Vacation Request"
        subtitle="Submit leave dates, balance-aware entitlement details, contact information, and optional attachments."
        actions={[
          <Button key="back" variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/requests/new')}>
            Request Catalog
          </Button>,
        ]}
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 370px' },
          gap: 1.5,
          alignItems: 'start',
        }}
      >
        <SectionCard
          tone="dashboard"
          title="Leave Details"
          subheader="Required fields are leave type, start date, and end date."
          contentSx={{ p: { xs: 1.35, md: 1.7 } }}
        >
          <Stack spacing={1.5}>
            {error ? <Alert severity="error">{error}</Alert> : null}

            {validationErrors.length ? (
              <Alert severity="error">
                <List dense disablePadding>
                  {validationErrors.map((msg) => (
                    <ListItem key={msg} sx={{ py: 0 }}>
                      {msg}
                    </ListItem>
                  ))}
                </List>
              </Alert>
            ) : null}

            {hasPendingRequest ? (
              <Alert severity="warning">You already have a vacation request pending approval.</Alert>
            ) : null}

            <Paper
              variant="outlined"
              sx={{
                p: 1.15,
                borderRadius: theme.hr?.radius?.md || 2,
                borderColor: alpha(theme.palette.primary.main, 0.16),
                bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.1 : 0.035),
              }}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <ChecklistRoundedIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                    Submission readiness
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap">
                  <Chip size="small" color={completionPct === 100 ? 'success' : 'warning'} variant="outlined" label={`${completionPct}% complete`} sx={{ fontWeight: 850 }} />
                  <Chip
                    size="small"
                    color={canSubmit ? 'success' : 'warning'}
                    icon={<VerifiedRoundedIcon />}
                    label={canSubmit ? 'Can submit' : 'Blocked'}
                    sx={{ fontWeight: 850 }}
                  />
                </Stack>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={completionPct}
                sx={{
                  mt: 1,
                  height: 7,
                  borderRadius: 999,
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 999,
                    background: theme.hr?.gradient?.success,
                  },
                }}
              />
              {blockingReasons.length ? (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 850 }}>
                    To submit, resolve:
                  </Typography>
                  <List dense disablePadding sx={{ mt: 0.4 }}>
                    {blockingReasons.map((reason) => (
                      <ListItem key={reason} sx={{ py: 0, pl: 1 }}>
                        <ListItemText
                          primary={reason}
                          primaryTypographyProps={{ variant: 'caption', sx: { lineHeight: 1.4 } }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              ) : null}
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 850, mr: 0.5 }}>
                  Routes to:
                </Typography>
                {routingSteps.slice(1).map((step, idx, arr) => (
                  <React.Fragment key={`route-${step.role || step.actor}-${idx}`}>
                    <Tooltip
                      title={step.role && step.actor !== step.role ? `${step.role}` : ''}
                      placement="top"
                      arrow
                      disableInteractive
                    >
                      <Chip
                        size="small"
                        variant={step.missing ? 'filled' : 'outlined'}
                        color={step.missing ? 'warning' : 'default'}
                        label={step.actor}
                        sx={{ fontWeight: 800 }}
                      />
                    </Tooltip>
                    {idx < arr.length - 1 ? (
                      <Typography variant="caption" color="text.secondary">
                        →
                      </Typography>
                    ) : null}
                  </React.Fragment>
                ))}
              </Box>
            </Paper>

            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={1.6}>
                <FieldGroup
                  icon={<CalendarMonthRoundedIcon color="primary" fontSize="small" />}
                  title="Leave Window"
                  description="Choose leave type and dates. Annual leave checks available balance and projected accrual."
                >
                  <FieldGrid>
                    <FormControl fullWidth required sx={fieldSx}>
                      <InputLabel>Leave Type</InputLabel>
                      <Select name="leave_type" label="Leave Type" value={formData.leave_type} onChange={handleChange}>
                        {getLeaveTypesForGender(gender).map((item) => (
                          <MenuItem key={item.value} value={item.value}>
                            {item.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      label="Start Date"
                      type="date"
                      name="start_date"
                      value={formData.start_date}
                      onChange={handleChange}
                      fullWidth
                      required
                      InputLabelProps={{ shrink: true }}
                      inputProps={
                        BACK_DATE_ALLOWED_TYPES.has(formData.leave_type) ? undefined : { min: todayStr }
                      }
                      helperText={
                        BACK_DATE_ALLOWED_TYPES.has(formData.leave_type)
                          ? 'Back-dated entries allowed for this leave type'
                          : 'Cannot be earlier than today'
                      }
                      sx={fieldSx}
                    />
                    <TextField
                      label="End Date"
                      type="date"
                      name="end_date"
                      value={formData.end_date}
                      onChange={handleChange}
                      fullWidth
                      required
                      InputLabelProps={{ shrink: true }}
                      inputProps={formData.start_date ? { min: formData.start_date } : undefined}
                      helperText={suggestedEndDate ? `Suggested: ${suggestedEndDate}` : 'Select an end date'}
                      sx={fieldSx}
                    />
                    <Button
                      variant="outlined"
                      onClick={suggestEndDate}
                      startIcon={<AutoFixHighRoundedIcon />}
                      sx={{
                        minHeight: 56,
                        borderRadius: theme.hr?.radius?.md || 2,
                        justifyContent: 'flex-start',
                      }}
                      fullWidth
                    >
                      Suggest Best End Date
                    </Button>
                  </FieldGrid>
                </FieldGroup>

                <Divider />

                <FieldGroup
                  icon={<PublicRoundedIcon color="primary" fontSize="small" />}
                  title="Contact During Leave"
                  description="Optional details for HR or managers if they need to reach you."
                >
                  <FieldGrid>
                    <TextField
                      label="Country (optional)"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      fullWidth
                      sx={fieldSx}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PublicRoundedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      label="Phone Number (optional)"
                      name="phone_number"
                      value={formData.phone_number}
                      onChange={handleChange}
                      fullWidth
                      sx={fieldSx}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIphoneRoundedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Box sx={{ gridColumn: { xs: 'auto', md: '1 / -1' } }}>
                      <TextField
                        label="Location in Country (optional)"
                        name="location_in_country"
                        value={formData.location_in_country}
                        onChange={handleChange}
                        fullWidth
                        sx={fieldSx}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PlaceRoundedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                  </FieldGrid>
                </FieldGroup>

                <Divider />

                <FieldGroup
                  icon={<EventAvailableRoundedIcon color="primary" fontSize="small" />}
                  title="Notes & Attachment"
                  description="Add reviewer context and an optional support file."
                >
                  <Stack spacing={1.2}>
                    <TextField
                      label="Description (optional)"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      fullWidth
                      multiline
                      rows={3}
                      sx={fieldSx}
                    />

                    <Paper
                      variant="outlined"
                      sx={{
                        borderRadius: theme.hr?.radius?.md || 2,
                        p: 1,
                        borderStyle: 'dashed',
                        borderColor: alpha(theme.palette.primary.main, 0.28),
                        bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.08 : 0.025),
                      }}
                    >
                      <Button
                        variant="text"
                        component="label"
                        fullWidth
                        color={attachmentNeeded && !file ? 'error' : 'primary'}
                        startIcon={<AttachFileRoundedIcon />}
                        sx={{ justifyContent: 'flex-start', px: 1 }}
                      >
                        {attachmentNeeded
                          ? `Upload Supporting Document (required for ${labelForLeaveType(formData.leave_type)})`
                          : 'Upload Attachment (optional)'}
                        <input type="file" name="attachment" accept=".jpg,.jpeg,.png,.pdf,.docx,.doc" hidden onChange={handleFileChange} />
                      </Button>
                      <Typography variant="caption" color="text.secondary" sx={{ px: 1, display: 'block' }}>
                        Allowed: JPG, JPEG, PNG, PDF, DOCX, DOC
                      </Typography>
                      {file ? (
                        <Chip
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ mt: 1, ml: 1, maxWidth: 'calc(100% - 16px)' }}
                          label={`Selected: ${file.name}`}
                        />
                      ) : null}
                    </Paper>
                  </Stack>
                </FieldGroup>

                <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1} justifyContent="flex-end">
                  <Button type="button" variant="outlined" color="inherit" startIcon={<RestartAltRoundedIcon />} onClick={handleReset} disabled={loading}>
                    Reset
                  </Button>
                  <Tooltip title={canSubmit ? '' : disabledReason} placement="top" arrow disableInteractive>
                    <Box component="span">
                      <Button
                        type="submit"
                        variant="contained"
                        startIcon={loading ? null : <SendRoundedIcon />}
                        disabled={!canSubmit}
                        aria-disabled={!canSubmit}
                      >
                        {loading ? <CircularProgress size={22} color="inherit" /> : 'Submit Request'}
                      </Button>
                    </Box>
                  </Tooltip>
                </Stack>
              </Stack>
            </Box>
          </Stack>
        </SectionCard>

        <Stack spacing={1.5} sx={{ position: { lg: 'sticky' }, top: { lg: 88 } }}>
          <SectionCard
            tone="dashboard"
            title="Leave Summary"
            subheader="Balance, dates, and request impact update as you work."
            contentSx={{ p: { xs: 1.35, md: 1.6 } }}
          >
            <Stack spacing={1.2}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  gap: 1,
                }}
              >
                <SummaryTile label="Base Balance" value={baseBalance.toFixed(2)} hint="days" tone="success" />
                <SummaryTile label="Requested" value={requestedDays} hint="days" tone={exceedsBalance ? 'warning' : 'primary'} />
                <SummaryTile label="Max Possible" value={maxPossibleVacation.toFixed(2)} hint="days" tone="info" />
                <SummaryTile label="Usage" value={`${usagePct}%`} hint="of available" tone={exceedsBalance ? 'error' : 'secondary'} />
              </Box>

              {!skipBalanceCheck ? (
                <Box>
                  <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 0.65 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 850 }}>
                      Balance usage
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 900 }}>
                      {requestedDays} / {maxPossibleVacation.toFixed(2)}
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={usagePct}
                    color={exceedsBalance ? 'error' : 'primary'}
                    sx={{ height: 7, borderRadius: 999 }}
                  />
                </Box>
              ) : (
                <Alert severity="info">{formData.leave_type} requests can be submitted without balance restriction.</Alert>
              )}

              {exceedsBalance ? (
                <Alert severity="warning">You are requesting more days than available including future accrual.</Alert>
              ) : null}

              {formData.leave_type === 'Annual' ? (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1,
                    borderRadius: theme.hr?.radius?.md || 2,
                    borderColor: alpha(theme.palette.info.main, 0.18),
                    bgcolor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.1 : 0.045),
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                    Annual Accrual Forecast
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
                    Accrual until Dec 31: {accrualDays.toFixed(2)} days
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Potential year-end balance: {potentialTotal.toFixed(2)} days
                  </Typography>
                </Paper>
              ) : null}

              <Divider />

              <SummaryRow icon={<CalendarMonthRoundedIcon fontSize="small" />} label="Start Date" value={formData.start_date} />
              <SummaryRow icon={<TimelineRoundedIcon fontSize="small" />} label="End Date" value={formData.end_date} />
              <SummaryRow icon={<PublicRoundedIcon fontSize="small" />} label="Country" value={formData.country || 'Country not set'} />
              <SummaryRow icon={<PhoneIphoneRoundedIcon fontSize="small" />} label="Phone" value={formData.phone_number || 'Phone not set'} />
              <SummaryRow icon={<PlaceRoundedIcon fontSize="small" />} label="Location" value={formData.location_in_country || 'Location not set'} />
            </Stack>
          </SectionCard>

          <SectionCard
            tone="dashboard"
            title="Approval Path"
            subheader="Expected review sequence after submission."
            contentSx={{ p: { xs: 1.25, md: 1.45 } }}
          >
            <Stack spacing={1}>
              {routingSteps.map((step, index) => (
                <Box
                  key={`${step.role || step.actor}-${index}`}
                  sx={{
                    p: 1,
                    borderRadius: theme.hr?.radius?.md || 2,
                    border: '1px solid',
                    borderColor: alpha(theme.palette.primary.main, 0.14),
                    bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.08 : 0.025),
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Chip size="small" label={`Step ${index + 1}`} variant="outlined" color="primary" sx={{ minWidth: 66, fontWeight: 800 }} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Stack direction="row" spacing={0.6} alignItems="center" flexWrap="wrap">
                        <Typography variant="body2" sx={{ fontWeight: 900 }}>
                          {step.actor}
                        </Typography>
                        {step.role && step.role !== step.actor ? (
                          <Chip size="small" variant="outlined" label={step.role} sx={{ height: 18, fontSize: '0.65rem', fontWeight: 800 }} />
                        ) : null}
                        {step.missing ? (
                          <Chip size="small" color="warning" variant="outlined" label="not assigned" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 800 }} />
                        ) : null}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {step.action}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </SectionCard>
        </Stack>
      </Box>
    </PageLayout>
  );
}

export default VacationRequestForm;
